import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["nameInput", "subdomainInput", "preview", "error"];
  static values = { domain: String };

  connect() {
    this.checkAvailability = this.debounce(
      this.checkAvailability.bind(this),
      500,
    );
  }

  nameInputTargetConnected() {
    this.generateSubdomain();
  }

  generateSubdomain() {
    if (!this.hasNameInputTarget || !this.hasSubdomainInputTarget) return;

    const name = this.nameInputTarget.value.trim();
    if (!name) {
      this.subdomainInputTarget.value = "";
      this.updatePreview("");
      return;
    }

    // Generate subdomain from company name (match Company model logic)
    const subdomain = name.toLowerCase().replace(/[^a-z0-9]/g, ""); // Remove all non-alphanumeric characters

    this.subdomainInputTarget.value = subdomain;
    this.updatePreview(subdomain);
    this.checkAvailability();
  }

  subdomainChanged() {
    const subdomain = this.subdomainInputTarget.value.trim();
    this.updatePreview(subdomain);
    this.checkAvailability();
  }

  updatePreview(subdomain) {
    if (!this.hasPreviewTarget) return;

    if (subdomain) {
      this.previewTarget.textContent = `${subdomain}.${this.domainValue}`;
      this.previewTarget.classList.remove("text-gray-400");
      this.previewTarget.classList.add("text-gray-900");
    } else {
      this.previewTarget.textContent = `your-company.${this.domainValue}`;
      this.previewTarget.classList.remove("text-gray-900");
      this.previewTarget.classList.add("text-gray-400");
    }
  }

  async checkAvailability() {
    const subdomain = this.subdomainInputTarget.value.trim();

    if (!subdomain) {
      this.clearError();
      return;
    }

    // Basic validation
    if (subdomain.length < 3) {
      this.showError("Subdomain must be at least 3 characters long");
      return;
    }

    if (!/^[a-z0-9]+$/.test(subdomain)) {
      this.showError("Subdomain can only contain letters and numbers");
      return;
    }

    // Check reserved subdomains
    const reserved = [
      "www",
      "mail",
      "remote",
      "webmail",
      "server",
      "smtp",
      "ftp",
      "vpn",
      "email",
      "cloud",
      "forum",
      "admin",
      "pop",
      "ssl",
      "sftp",
      "api",
      "cdn",
      "support",
      "static",
      "blog",
      "dashboard",
      "jobs",
      "status",
      "dev",
      "go",
      "beta",
      "app",
      "apps",
      "mobile",
      "m",
      "shop",
      "portal",
      "legal",
      "learn",
      "help",
      "enroll",
      "signup",
      "join",
      "public",
      "login",
      "get",
      "install",
      "pay",
      "payment",
      "storage",
      "invoice",
      "inbox",
      "webhooks",
      "telecom",
      "twilio",
      "twiliorhb",
      "stripehb",
      "stripe",
    ];

    if (reserved.includes(subdomain.toLowerCase())) {
      this.showError("This subdomain is reserved and cannot be used");
      return;
    }

    try {
      const response = await fetch(
        `/api/subdomains/check?subdomain=${encodeURIComponent(subdomain)}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.available) {
          this.clearError();
        } else {
          this.showError("This subdomain is already taken");
        }
      }
    } catch (error) {
      // Silently fail API check - we'll validate server-side anyway
      console.warn("Could not check subdomain availability:", error);
      this.clearError();
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }

    this.subdomainInputTarget.classList.add(
      "border-red-300",
      "focus:border-red-500",
      "focus:ring-red-500",
    );
    this.subdomainInputTarget.classList.remove(
      "border-gray-300",
      "focus:border-blue-500",
      "focus:ring-blue-500",
    );
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }

    this.subdomainInputTarget.classList.remove(
      "border-red-300",
      "focus:border-red-500",
      "focus:ring-red-500",
    );
    this.subdomainInputTarget.classList.add(
      "border-gray-300",
      "focus:border-blue-500",
      "focus:ring-blue-500",
    );
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}
